import { z } from "zod";
// The staff fulfilment evidence contract of Milestone D, lane R4 (CHT-752; docs/architecture/milestone-d-contracts.md, sections 7.6 and 8).
// Routes MD-R12 (evidence), MD-R13 (customer updates) and the attachment routes, in `apps/api/src/routes/staff-request-evidence-routes.ts`.
// The admin calls them only through `@lafayette/api-client` (`request-fulfilment.ts`).
//
// STAFF ONLY: every response shape here holds data a customer must never see (the supplier, the contact, the reference, the detail, the
// storage key). Nothing in this file is served on a customer route. The customer's own view of the human work (`updates`, `attachments`) is in
// `domain-views.ts`, with a closed key list of its own.
//
// NO BODY CARRIES AN AMOUNT. There is no price, amount or currency key anywhere: every body is a strict object, so a client that sends one
// gets a `400`. The only amount source of a payment is the approved `payment_quotes` row; a `QUOTE` evidence row may only POINT at one
// (`paymentQuoteId`).
//
// Closed codes are repeated from the domain and parity-tested (`staff-request-evidence-routes.test.ts` compares each list with its tuple).
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/u;
const uuid = z.string().regex(UUID);
const instant = z.iso.datetime({ offset: false });
/** A time typed by staff: ISO 8601 with an offset or `Z`, read as a `Date`. */
const typedInstant = z.iso.datetime({ offset: true }).transform((value) => new Date(value));
const version = z.number().int().min(1).max(2_147_483_647);
/** A staff-internal reason. Never a customer view, a card, a notice or analytics. */
const staffReason = z.string().trim().min(1).max(500);
const nullableText = (max) => z.string().trim().min(1).max(max).nullish();
// --- codes (parity-tested) --------------------------------------------------------------------------------------------------------
export const fulfilmentEvidenceKindValues = [
    "SUPPLIER_CONTACT",
    "AVAILABILITY",
    "QUOTE",
    "CONFIRMATION_REFERENCE",
    "UNAVAILABLE_REASON",
    "CORRECTION",
];
export const fulfilmentContactMethodValues = [
    "PHONE",
    "EMAIL",
    "WHATSAPP",
    "IN_PERSON",
    "OTHER",
];
export const fulfilmentConfirmationKindValues = [
    "SUPPLIER_WRITTEN_CONFIRMATION",
    "SUPPLIER_VERBAL_CONFIRMATION",
    "LAFAYETTE_DIRECT_FULFILMENT",
];
export const customerUpdateKindValues = [
    "NEXT_STEP",
    "CONFIRMATION_DETAILS",
    "ALTERNATIVES",
    "UNAVAILABLE_NOTE",
    "RESTRICTIONS",
];
export const requestAttachmentContentTypeValues = [
    "application/pdf",
    "image/jpeg",
    "image/png",
    "image/webp",
];
export const attachmentVisibilityValues = ["STAFF_ONLY", "SHARED"];
// --- bodies -----------------------------------------------------------------------------------------------------------------------
/**
 * MD-R12 record evidence. Strict: an unknown key (an amount, a price, `observedAt`) is a `400`. Which fields a kind needs is the domain's rule
 * (`validateFulfilmentEvidence`, mirrored by database CHECKs) and is answered as `422`.
 */
export const staffRequestEvidenceBodySchema = z.strictObject({
    kind: z.enum(fulfilmentEvidenceKindValues),
    contactMethod: z.enum(fulfilmentContactMethodValues).nullish(),
    supplierLabel: nullableText(200),
    /** When the supplier said it. */
    sourceTimestamp: typedInstant.nullish(),
    /** `CONFIRMATION_REFERENCE` only, and required there. */
    referenceCode: nullableText(200),
    /** `CONFIRMATION_REFERENCE` only, and required there. */
    confirmationKind: z.enum(fulfilmentConfirmationKindValues).nullish(),
    /** `QUOTE` and `AVAILABILITY` only. */
    expiresAt: typedInstant.nullish(),
    /** `QUOTE` only: an existing approved quote of this request. */
    paymentQuoteId: uuid.nullish(),
    detail: nullableText(2000),
    /** `CORRECTION` only, and required there. */
    supersedesEvidenceId: uuid.nullish(),
});
/** MD-R13 publish a customer update: text the staff member wrote, approved by publishing it. */
export const staffRequestCustomerUpdateBodySchema = z.strictObject({
    kind: z.enum(customerUpdateKindValues),
    text: z.string().trim().min(1).max(1000),
    /** The evidence this update rests on. A staff reference only: the customer never sees it. */
    evidenceId: uuid.nullish(),
});
/**
 * Ask for a signed upload target for one file (CHT-754). The type and the size are what the browser says; the server checks the bytes
 * again when the upload is completed. No path and no file name: the server makes the key.
 */
export const staffRequestAttachmentUploadTargetBodySchema = z.strictObject({
    contentType: z.enum(requestAttachmentContentTypeValues),
    sizeBytes: z.number().int().min(1).max(10_485_760),
});
/**
 * Where the browser sends the file. `uploadId` names the upload in the completion call. The URL and the headers belong to one exact
 * key (which is not returned) and expire at `expiresAt`.
 */
export const staffRequestAttachmentUploadTargetSchema = z.strictObject({
    uploadId: uuid,
    url: z.string().min(1),
    method: z.enum(["PUT", "POST"]),
    headers: z.record(z.string(), z.string()),
    expiresAt: instant,
});
/**
 * Attach an uploaded file to a request (CHT-754). `displayName` is a label: the server cleans it and never puts it in a key. The size
 * is not in the body: the server reads it from the object store.
 */
export const staffRequestAttachmentBodySchema = z.strictObject({
    uploadId: uuid,
    displayName: z.string().min(1).max(255),
    contentType: z.enum(requestAttachmentContentTypeValues),
});
/** A signed read URL for one file, for staff. It lives about a minute and the answer is never cached. */
export const staffRequestAttachmentDownloadSchema = z.strictObject({
    attachmentId: uuid,
    displayName: z.string(),
    contentType: z.enum(requestAttachmentContentTypeValues),
    sizeBytes: z.number().int(),
    url: z.string().min(1),
    expiresAt: instant,
});
/** Share an attachment with the customer, on purpose and with a reason. One way and once. */
export const staffRequestAttachmentShareBodySchema = z.strictObject({ reason: staffReason });
/** Mark a request unavailable with the internal reason and the customer-safe alternatives, in one transaction. */
export const staffRequestUnavailableWithAlternativesBodySchema = z.strictObject({
    expectedVersion: version,
    reason: staffReason,
    /** Staff only: why it is unavailable. */
    detail: z.string().trim().min(1).max(2000),
    supplierLabel: nullableText(200),
    contactMethod: z.enum(fulfilmentContactMethodValues).nullish(),
    /** What the customer reads as the reason. */
    customerNote: nullableText(1000),
    /** What the customer reads as alternatives. */
    alternatives: nullableText(1000),
    customerStatusSummary: nullableText(500),
});
/** Correct a confirmation without editing anything: a CORRECTION row and a customer update with the explicit marker. */
export const staffRequestCorrectionBodySchema = z.strictObject({
    expectedVersion: version,
    reason: staffReason,
    /** Staff only: what was wrong and what is right. */
    detail: z.string().trim().min(1).max(2000),
    /** What the customer reads, marked as a correction. */
    customerText: z.string().trim().min(1).max(1000),
    customerUpdateKind: z.enum(["CONFIRMATION_DETAILS", "NEXT_STEP", "RESTRICTIONS"]).nullish(),
});
// --- answers ----------------------------------------------------------------------------------------------------------------------
/** The answer of a recording: ids only. What was typed is not echoed. */
export const staffRequestEvidenceResultSchema = z.strictObject({
    requestId: uuid,
    evidenceId: uuid,
});
export const staffRequestCustomerUpdateResultSchema = z.strictObject({
    requestId: uuid,
    updateId: uuid,
});
export const staffRequestAttachmentResultSchema = z.strictObject({
    requestId: uuid,
    attachmentId: uuid,
});
/** One evidence row, staff view. */
export const staffEvidenceViewSchema = z.strictObject({
    id: uuid,
    kind: z.enum(fulfilmentEvidenceKindValues),
    contactMethod: z.enum(fulfilmentContactMethodValues).nullable(),
    supplierLabel: z.string().nullable(),
    observedAt: instant,
    sourceTimestamp: instant.nullable(),
    referenceCode: z.string().nullable(),
    confirmationKind: z.enum(fulfilmentConfirmationKindValues).nullable(),
    expiresAt: instant.nullable(),
    paymentQuoteId: uuid.nullable(),
    detail: z.string().nullable(),
    supersedesEvidenceId: uuid.nullable(),
    /** The row that corrects this one. A corrected row can no longer confirm. */
    supersededByEvidenceId: uuid.nullable(),
    /** This row confirmed the request. */
    isConfirmation: z.boolean(),
    /** A confirmation row that may confirm the request now: fresh and not corrected. */
    canConfirm: z.boolean(),
    createdByStaffUserId: uuid,
    createdAt: instant,
});
export const staffCustomerUpdateViewSchema = z.strictObject({
    id: uuid,
    kind: z.enum(customerUpdateKindValues),
    text: z.string(),
    evidenceId: uuid.nullable(),
    isCorrection: z.boolean(),
    createdByStaffUserId: uuid,
    createdAt: instant,
});
export const staffAttachmentViewSchema = z.strictObject({
    id: uuid,
    displayName: z.string(),
    storagePath: z.string(),
    contentType: z.enum(requestAttachmentContentTypeValues),
    sizeBytes: z.number().int(),
    visibility: z.enum(attachmentVisibilityValues),
    sharedAt: instant.nullable(),
    sharedByStaffUserId: uuid.nullable(),
    createdByStaffUserId: uuid,
    createdAt: instant,
});
/** MD-R12 `GET .../evidence`: everything staff recorded about the human work of one request. Needs `request.read_internal`. */
export const staffFulfilmentViewSchema = z.strictObject({
    requestId: uuid,
    evidence: z.array(staffEvidenceViewSchema),
    customerUpdates: z.array(staffCustomerUpdateViewSchema),
    attachments: z.array(staffAttachmentViewSchema),
    /** `true` when older rows were left out of the newest window of that list. */
    evidenceTruncated: z.boolean(),
    customerUpdatesTruncated: z.boolean(),
    attachmentsTruncated: z.boolean(),
});
